import { Component, Element, h, Host, Listen, Prop, State } from '@stencil/core'

import { Breakpoint, BreakpointObserver, BREAKPOINTS } from '../../../utils/breakpoint-observer'
import Helpers from '../../../utils/helpers'
import { CssClassMap } from '../../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/header?tab=designer).
 *
 * @slot default - Slot para o nome ou rótulo da funcionalidade.
 * @slot icon - Slot para ícone que representa a funcionalidade.
 */
@Component({
  tag: 'br-header-function',
  styleUrl: 'header-function.scss',
  shadow: true,
})
export class HeaderFunction {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrHeaderFunctionElement

  /**
   * Identificador único; gerado automaticamente quando omitido.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * URL de destino da funcionalidade.
   */
  @Prop({ reflect: true }) readonly href: string = null

  /**
   * Nome do ícone exibido.
   */
  @Prop({ reflect: true }) readonly iconName: string = null

  /**
   * Define o alvo do link quando `href` está presente. Pode ser:
   * - `_blank` para abrir em uma nova aba,
   * - `_self` para abrir na mesma aba,
   * - `_parent` para abrir na aba pai,
   * - `_top` para abrir na aba superior.
   */
  @Prop({ reflect: true }) readonly target?: '_blank' | '_self' | '_parent' | '_top' = '_self'

  /**
   * Breakpoint atual.
   */
  @State() currentBreakpoint: Breakpoint = null

  /**
   * Indica se a lista está encolhido.
   */
  @State() isShrunk: boolean = false

  /**
   * Observador para acompanhar mudanças de breakpoint.
   */
  private breakpointObserver: BreakpointObserver = null

  /**
   * Listener para o evento 'headerWidthChange', disparado pelo componente pai 'br-header'.
   * O evento contém o ID do pai e o nome da lista.
   * Se o ID do pai corresponder ao ID do cabeçalho atual, o evento será tratado.
   * @param event O evento customizado contendo o ID do pai e o nome da lista.
   */
  @Listen('headerWidthChange', { target: 'document', passive: true })
  handleHeaderWidthChange(event: CustomEvent<{ id: string; listNames: string[]; shrink: boolean }>) {
    if (event.detail.id === this.el.parentElement.parentElement.shadowRoot.querySelector('.br-header').id) {
      if (event.detail.listNames.includes('functions')) {
        this.isShrunk = event.detail.shrink
      }
    }
  }

  /**
   * Ciclo de vida do componente.
   * Executado quando o componente é conectado ao DOM.
   */
  connectedCallback() {
    this.setBreakpointObserver()
  }

  /**
   * Ciclo de vida do componente.
   * Executado quando o componente é carregado.
   */
  componentDidLoad() {
    void this.handleHeaderWidthChange
  }

  /**
   * Mapeia os nomes das classes CSS a serem aplicadas ao componente.
   * @returns Objeto com os nomes das classes CSS a serem aplicadas ao componente.
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-header-function': true,
    }
  }

  /**
   * Ciclo de vida do componente.
   * Executado quando o componente é desconectado do DOM.
   */
  disconnectedCallback() {
    this.breakpointObserver?.unsubscribe(this.handleBreakpointChange)
    this.breakpointObserver.destroy()
  }

  /**
   * Manipula as mudanças de breakpoint.
   * @param breakpoint O breakpoint ativo.
   */
  private handleBreakpointChange = (breakpoint: Breakpoint) => {
    this.currentBreakpoint = breakpoint
  }

  /**
   * Configura o observador de mudanças de breakpoint.
   */
  private setBreakpointObserver() {
    this.breakpointObserver = new BreakpointObserver()
    this.breakpointObserver.subscribe(this.handleBreakpointChange)
  }

  private renderFunctionButton() {
    return (
      <br-button emphasis="tertiary" shape="circle" density="small" slot="start">
        <slot name="icon">
          <br-icon icon-name={this.iconName}></br-icon>
        </slot>
      </br-button>
    )
  }

  private renderFunctionItem() {
    return (
      <br-item id={this.customId} class={this.getCssClassMap()} density="large" href={this.href} target={this.target}>
        <slot name="icon" slot="start">
          <br-icon icon-name={this.iconName}></br-icon>
        </slot>
        <slot></slot>
      </br-item>
    )
  }

  /**
   * Renderiza o componente.
   * @returns JSX do componente.
   */
  render() {
    return (
      <Host>
        {/* {this.isShrunk ?
          this.renderFunctionItem()
          : BreakpointObserver.getBreakpoints('until', BREAKPOINTS.MEDIUM).includes(this.currentBreakpoint) ?
            this.renderFunctionItem()
            : this.renderFunctionButton()
        } */}
        {BreakpointObserver.getBreakpoints('above', BREAKPOINTS.MEDIUM).includes(this.currentBreakpoint) &&
        !this.isShrunk
          ? this.renderFunctionButton()
          : null}
        {BreakpointObserver.getBreakpoints('until', BREAKPOINTS.MEDIUM).includes(this.currentBreakpoint) ||
        this.isShrunk
          ? this.renderFunctionItem()
          : null}
      </Host>
    )
  }
}
